"use client"

import Script from "next/script"
import { useCallback, useEffect, useRef, useState } from "react"
import { TURNSTILE_ACTION } from "@/lib/turnstile/constants"

type TurnstileRenderOptions = {
  sitekey: string
  action: string
  theme: "light" | "dark" | "auto"
  size: "flexible" | "compact"
  callback: (token: string) => void
  "expired-callback": () => void
  "error-callback": () => void
}

type TurnstileApi = {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string
  remove: (widgetId: string) => void
  reset: (widgetId: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

type TurnstileWidgetProps = {
  onTokenChange: (token: string) => void
  resetKey?: number
  theme?: "light" | "dark" | "auto"
}

const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || ""

export function TurnstileWidget({
  onTokenChange,
  resetKey = 0,
  theme = "auto",
}: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)
  const widgetSizeRef = useRef<"flexible" | "compact" | null>(null)
  const callbackRef = useRef(onTokenChange)
  const [scriptReady, setScriptReady] = useState(false)
  const [widgetError, setWidgetError] = useState(false)
  const [widgetSize, setWidgetSize] = useState<"flexible" | "compact">(
    "flexible",
  )

  useEffect(() => {
    callbackRef.current = onTokenChange
  }, [onTokenChange])

  const renderWidget = useCallback(() => {
    if (
      !siteKey ||
      !containerRef.current ||
      !window.turnstile
    ) {
      return
    }

    const nextSize =
      containerRef.current.clientWidth > 0 &&
      containerRef.current.clientWidth < 300
        ? "compact"
        : "flexible"

    if (widgetIdRef.current && widgetSizeRef.current === nextSize) return

    if (widgetIdRef.current) {
      window.turnstile.remove(widgetIdRef.current)
      widgetIdRef.current = null
      callbackRef.current("")
    }

    widgetSizeRef.current = nextSize
    setWidgetSize(nextSize)
    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: siteKey,
      action: TURNSTILE_ACTION,
      theme,
      size: nextSize,
      callback(token) {
        setWidgetError(false)
        callbackRef.current(token)
      },
      "expired-callback"() {
        callbackRef.current("")
      },
      "error-callback"() {
        callbackRef.current("")
        setWidgetError(true)
      },
    })
  }, [theme])

  useEffect(() => {
    if (window.turnstile) {
      setScriptReady(true)
      renderWidget()
    }
  }, [renderWidget])

  useEffect(() => {
    if (scriptReady) renderWidget()
  }, [renderWidget, scriptReady])

  useEffect(() => {
    if (!containerRef.current || typeof ResizeObserver === "undefined") return

    const observer = new ResizeObserver(() => {
      if (scriptReady) renderWidget()
    })
    observer.observe(containerRef.current)

    return () => observer.disconnect()
  }, [renderWidget, scriptReady])

  useEffect(() => {
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current)
      callbackRef.current("")
      setWidgetError(false)
    }
  }, [resetKey])

  useEffect(
    () => () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current)
        widgetIdRef.current = null
        widgetSizeRef.current = null
      }
    },
    [],
  )

  if (!siteKey) {
    return (
      <p className="rounded-xl border border-rose-700/25 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-800">
        Human verification is temporarily unavailable.
      </p>
    )
  }

  return (
    <div className="min-w-0 space-y-2">
      <Script
        id="cloudflare-turnstile"
        src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
        strategy="afterInteractive"
        onReady={() => {
          setScriptReady(true)
          renderWidget()
        }}
      />
      <div
        ref={containerRef}
        className={`cf-turnstile flex w-full min-w-0 justify-center ${
          widgetSize === "compact" ? "min-h-[140px]" : "min-h-[65px]"
        }`}
        data-sitekey={siteKey}
        data-action={TURNSTILE_ACTION}
        data-size={widgetSize}
        aria-label="Human verification"
      />
      {widgetError ? (
        <p className="text-sm font-medium text-rose-600" role="alert">
          Verification could not load. Please retry or refresh the page.
        </p>
      ) : null}
    </div>
  )
}
